import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Camera, Image as ImageIcon } from "lucide-react";
import moment from "moment";

/* ─── India-specific field validation ─── */
const MIN_RENTAL_AGE = 21; // T&C age criteria
const MAX_AGE = 100;
const DL_STATE_CODES = "AN AP AR AS BR CG CH CT DD DL DN GA GJ HP HR JH JK KA KL LA LD MH ML MN MP MZ NL OD OR PB PY RJ SK TG TN TR TS UA UK UP WB".split(" ");

// Verhoeff tables: an Aadhaar's last digit is a Verhoeff check digit
const VERHOEFF_D = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9], [1, 2, 3, 4, 0, 6, 7, 8, 9, 5], [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7], [4, 0, 1, 2, 3, 9, 5, 6, 7, 8], [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2], [7, 6, 5, 9, 8, 2, 1, 0, 4, 3], [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];
const VERHOEFF_P = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9], [1, 5, 7, 6, 2, 8, 3, 0, 9, 4], [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7], [9, 4, 5, 3, 1, 2, 6, 8, 7, 0], [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5], [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];

export const isValidEmail = (value) =>
  /^(?!.*\.\.)[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/.test(value);

// Accepts +91 / 91 / 0 prefixes; returns the bare 10-digit number, or null if invalid
export const normalizeIndianMobile = (value) =>
  String(value).replace(/[\s-]/g, "").match(/^(?:\+91|91|0)?([6-9]\d{9})$/)?.[1] || null;

export const isValidAadhaar = (digits) => {
  if (!/^[2-9]\d{11}$/.test(digits)) return false;
  const check = [...digits].reverse().reduce((c, digit, i) => VERHOEFF_D[c][VERHOEFF_P[i % 8][Number(digit)]], 0);
  return check === 0;
};

// Sarathi format: state (2) + RTO (2) + issue year (4) + serial (7); spaces, hyphens and slashes are ignored
export const normalizeDlNumber = (value) => value.toUpperCase().replace(/[\s\-/]/g, "");
export const isValidDlNumber = (normalized) =>
  /^[A-Z]{2}[0-9A-Z]{2}(19|20)\d{2}\d{7}$/.test(normalized) && DL_STATE_CODES.includes(normalized.slice(0, 2));

// Returns the message to show, or null when the DOB is fine
export const getDobError = (value) => {
  if (!value) return "Please provide DOB";
  const dob = moment(value, "YYYY-MM-DD", true);
  if (!dob.isValid()) return "DOB is not a valid date";
  if (dob.isAfter(moment())) return "DOB cannot be in the future";
  const age = moment().diff(dob, "years");
  if (age < MIN_RENTAL_AGE) return `You must be at least ${MIN_RENTAL_AGE} years old`;
  if (age > MAX_AGE) return "Please enter a valid DOB";
  return null;
};

/* ─── avatar with circular completion ring ─── */
export const AvatarProgress = ({ src, percent, size = 120, stroke = 4, gap = 3, showLabel = false, onPhotoClick, children }) => {
  const outer = size + (stroke + gap) * 2;
  const r = (outer - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const pct = Math.min(100, Math.max(0, Number(percent) || 0));
  return (
    <div className="relative shrink-0" style={{ width: outer, height: outer }}>
      <svg width={outer} height={outer} className="absolute inset-0 -rotate-90" aria-hidden="true">
        <circle cx={outer / 2} cy={outer / 2} r={r} fill="none" stroke="#f1f3f5" strokeWidth={stroke} />
        <circle
          cx={outer / 2} cy={outer / 2} r={r} fill="none" stroke="#2562ea" strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - pct / 100)}
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
      </svg>
      <button
        type="button"
        onClick={onPhotoClick}
        aria-label="View profile photo"
        className="absolute bg-transparent border-0 cursor-pointer overflow-hidden p-0 rounded-full"
        style={{ width: size, height: size, top: stroke + gap, left: stroke + gap }}
      >
        <img src={src} alt="avatar" className="block h-full object-cover w-full" />
      </button>
      {showLabel && percent != null && (
        <span className="absolute left-1/2 -translate-x-1/2 -bottom-2 bg-[#2562ea] text-white font-['Outfit'] font-semibold text-[11px] leading-none px-2 py-1 rounded-full border-2 border-white pointer-events-none">
          {Math.round(pct)}%
        </span>
      )}
      {children}
    </div>
  );
};

/* ─── full-screen profile photo; portalled to body so it sits above the navbar and mobile drawer ─── */
export const ProfilePhotoViewer = ({ src, onClose, onEditPhoto }) => {
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const pickFrom = (inputRef) => {
    setOptionsOpen(false);
    inputRef.current?.click();
  };

  const handlePick = async (e) => {
    const input = e.target;
    setUploading(true);
    await onEditPhoto(e);
    input.value = ""; // lets the same photo be picked again
    setUploading(false);
  };

  useEffect(() => {
    const closeOnEsc = (e) => { if (e.key === "Escape") onClose(); };
    const bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEsc);
    return () => {
      document.body.style.overflow = bodyOverflow;
      document.removeEventListener("keydown", closeOnEsc);
    };
  }, [onClose]);

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[10002] flex flex-col gap-5 items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.85)" }}
      onClick={optionsOpen ? () => setOptionsOpen(false) : onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Profile photo"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute bg-transparent border-0 flex items-center justify-center right-3 size-10 text-[24px] text-white top-3"
      >
        ✕
      </button>
      <motion.img
        src={src}
        alt="Profile"
        initial={{ scale: 0.85 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
        className={`${onEditPhoto ? "max-h-[70vh]" : "max-h-[80vh]"} max-w-[min(100%,520px)] object-contain rounded-[12px]`}
      />
      {onEditPhoto && (
        // Inputs stay in here: their programmatic click bubbles, and must not reach the backdrop's close handler
        <div onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => setOptionsOpen(true)}
            disabled={uploading}
            className="border-0 flex font-['Outfit'] font-semibold gap-2 h-11 items-center justify-center px-8 rounded-full text-[15px] text-white disabled:opacity-70"
            style={{ backgroundImage: "linear-gradient(2.28deg, #A28FDC 0.74%, #7C3AED 74.61%)" }}
          >
            {!uploading && <FigIcon name="avatar-pencil" size={14} />}
            {uploading ? "Uploading..." : "Edit"}
          </button>
          <input type="file" accept="image/*" className="hidden" ref={galleryInputRef} onChange={handlePick} />
          <input type="file" accept="image/*" capture="user" className="hidden" ref={cameraInputRef} onChange={handlePick} />
        </div>
      )}
      {optionsOpen && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
          className="absolute bg-white bottom-0 flex flex-col font-['Outfit'] gap-1 inset-x-0 pb-6 pt-3 px-4 rounded-t-[20px]"
        >
          <button
            type="button"
            onClick={() => pickFrom(galleryInputRef)}
            className="bg-transparent border-0 flex font-medium gap-3 h-12 items-center text-[#111827] text-[15px] w-full"
          >
            <ImageIcon size={20} className="text-[#7c3aed]" />
            Choose from gallery
          </button>
          <button
            type="button"
            onClick={() => pickFrom(cameraInputRef)}
            className="bg-transparent border-0 flex font-medium gap-3 h-12 items-center text-[#111827] text-[15px] w-full"
          >
            <Camera size={20} className="text-[#7c3aed]" />
            Take a photo
          </button>
          <button
            type="button"
            onClick={() => setOptionsOpen(false)}
            className="bg-transparent border-0 font-medium h-12 text-[#6b7280] text-[15px] w-full"
          >
            Cancel
          </button>
        </motion.div>
      )}
    </motion.div>,
    document.body,
  );
};

/* ─── icon wrappers matching Figma's icon-container style ─── */
export const IconBox = ({ children }) => (
  <div className="bg-[#fbf6f6] border border-[#e9e9e9] flex items-center justify-center rounded-[8px] shrink-0 w-8 h-7">
    {children}
  </div>
);

export const FigIcon = ({ name, size = 16 }) => (
  <img src={`/assets/profile/${name}.svg`} alt="" width={size} height={size} className="block shrink-0 max-w-none" style={{ width: size, height: size }} />
);

/* ─── profile summary row (label grey, value dark) ─── */
export const SummaryRow = ({ icon, label, value }) => (
  <div className="flex items-center gap-[14px] w-full rounded-[12px]">
    <IconBox><FigIcon name={icon} /></IconBox>
    <div className="flex flex-col gap-[2px] items-start flex-1 min-w-0">
      <p className="font-['Outfit'] font-medium text-[#8e8e93] text-[12px] w-full text-left">{label}</p>
      <p className="font-['Outfit'] font-semibold text-[#1c1c1e] text-[15px] w-full text-left truncate">{value}</p>
    </div>
  </div>
);

/* ─── personal information field (view / edit) ─── */
export const PersonalField = ({ id, icon, label, value, display, placeholder, type = "text", editing, onChange, onEdit, padding = "p-3" }) => (
  <div className={`flex flex-1 items-center justify-between min-w-0 ${padding} rounded-[12px] transition-colors ${editing ? "bg-[#f9fafb] ring-1 ring-[#e5e7eb]" : ""}`}>
    <div className="flex flex-1 gap-[14px] items-center min-w-0">
      <IconBox><FigIcon name={icon} /></IconBox>
      <div className="flex flex-col gap-[2px] items-start flex-1 min-w-0">
        <label htmlFor={id} className="font-['Outfit'] font-medium text-[#1c1c1e] text-[12px] w-full text-left">{label}</label>
        {editing ? (
          <input
            id={id}
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="font-['Outfit'] text-[14px] text-[#1c1c1e] bg-transparent outline-none w-full text-left placeholder:text-[#aeaeb2]"
          />
        ) : (
          <p className={`font-['Outfit'] text-[14px] w-full text-left truncate ${display ? "text-[#1c1c1e]" : "text-[#aeaeb2]"}`}>
            {display || placeholder}
          </p>
        )}
      </div>
    </div>
    <button type="button" onClick={onEdit} className="p-1 shrink-0" aria-label={`Edit ${label}`}>
      <FigIcon name="field-pencil" size={14} />
    </button>
  </div>
);

/* ─── document type dropdown (Figma: Collapsed / Expanded / Selected) ─── */
export const DocTypeDropdown = ({ options, value, onChange, compact = false, label }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const selected = options.find((o) => o.id === value);
  // compact scales with the viewport so the dropdown fits in a row beside other controls
  const textClass = compact ? "text-[clamp(12px,3.4vw,14px)]" : "text-[14px]";
  return (
    <div ref={ref} className="relative flex flex-col gap-1 w-full max-w-[295px]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`bg-white flex items-center rounded-[8px] w-full ${compact ? "px-[clamp(8px,2.6vw,12px)] py-[clamp(6px,1.9vw,8px)]" : "px-4 py-3"} ${open ? "border-[1.5px] border-[#5478f2]" : "border border-[#d1d5db]"}`}
      >
        <span className="flex flex-1 flex-col items-start min-w-0">
          {label && <span className="font-['Outfit'] font-medium leading-tight text-[#6b7280] text-[clamp(9px,2.6vw,10px)] whitespace-nowrap">{label}</span>}
          <span className={`w-full font-['Outfit'] ${textClass} text-left truncate ${selected ? "font-medium text-[#21262e]" : "font-normal text-[#9ca3af]"}`}>
            {selected ? selected.label : "Select document type"}
          </span>
        </span>
        <img src="/assets/profile/dropdown-icon.svg" alt="" width={24} height={24} className={`block shrink-0 max-w-none ${compact ? "size-[clamp(16px,5vw,20px)]" : "size-6"} ${open ? "-scale-y-100" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-20 bg-white border border-[#d1d5db] flex flex-col gap-[2px] p-[6px] rounded-[8px] shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04),0px_4px_12px_0px_rgba(0,0,0,0.08)] w-full max-h-[320px] overflow-y-auto">
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => { onChange(o.id); setOpen(false); }}
              className={`flex items-center ${compact ? "px-[clamp(6px,2.4vw,10px)]" : "px-[10px]"} py-[9px] rounded-[6px] w-full font-['Outfit'] ${textClass} text-[#21262e] text-left hover:bg-[#f3f4f6] ${o.id === value ? "bg-[#eef2ff]" : ""}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const SavedDocCard = ({ label, attachment, onView }) => {
  const src = attachment?.filePath || attachment?.url;
  return (
    <button
      type="button"
      onClick={() => src && onView?.({ ...attachment, filePath: src })}
      className="bg-white border border-[#0d9488] flex flex-col gap-1 flex-1 items-center justify-center p-4 rounded-[14px] min-w-0 cursor-pointer hover:bg-[#f0fdfa] transition-colors"
    >
      <p className="font-['Outfit'] font-bold text-[#0d9488] text-[14px]">{label} Saved</p>
      <p className="font-['Outfit'] font-normal text-[#6b7280] text-[11px] truncate w-full text-center">{attachment?.fileName || "document"}</p>
    </button>
  );
};

/* unsaved document form inputs: border only, no fill. !border is required: the input reset in index.css outranks .border on specificity */
export const kycInputClass = "bg-transparent !border border-[#d1d5db] h-12 px-4 rounded-[12px] w-full font-['Outfit'] text-[16px] outline-none focus:border-[#9E85DD] text-left";

export const ReadOnlyKycField = ({ label, value, outlined }) => (
  <div className="flex flex-col gap-1 w-full">
    <p className="font-['Outfit'] font-semibold text-[#1c1c1e] text-[14px] text-left">{label}</p>
    <div className={`border ${outlined ? "bg-transparent border-[#d1d5db]" : "bg-[#f9fafb] border-[#e5e5ea]"} min-h-12 px-4 py-2 rounded-[12px] w-full font-['Outfit'] text-[16px] text-[#6b7280] flex items-center break-all`}>{value || "—"}</div>
  </div>
);

export const fmtDate = (d) => (typeof d === "string" ? d.split(" ")[0] : d);

const getSavedDocStatus = (doc) => {
  if (doc?.isVerified) return { note: "Verified by our compliance team", icon: "✓", label: "VERIFIED", style: "border-[#0d9488] text-[#0d9488]" };
  if (doc?.isRejected) return { note: "Rejected — please upload this document again", icon: "✕", label: "REJECTED", style: "border-[#fecaca] bg-[#fef2f2] text-[#dc2626]" };
  return { note: "Saved — our compliance team will review it within 24 hours", icon: "⏳", label: "UNDER REVIEW", style: "border-[#fde68a] bg-[#fef3c7] text-[#d97706]" };
};

/* ─── saved (read-only) document column — under review, verified or rejected; username is left out for other documents ─── */
export const SavedKycColumn = ({ title, username, doc, idLabel, startLabel, endLabel, onView }) => {
  const status = getSavedDocStatus(doc);
  return (
  <div className="flex flex-1 flex-col gap-2 min-w-0">
    <div className="flex flex-col gap-[6px] w-full">
      <p className="font-['Outfit'] font-bold text-[#111827] text-[16px] text-left">{title}</p>
      <p className="font-['Outfit'] font-normal text-[#4b5563] text-[14px] leading-[22px] text-left">
        {status.note}
      </p>
    </div>
    {username && <p className="font-['Outfit'] font-semibold text-[#3c3c43] text-[14px] tracking-[0.5px] uppercase opacity-60 pt-1 text-left">Personal Details</p>}
    <div className="flex flex-col gap-4">
      {username && <ReadOnlyKycField label="User Name" value={username} />}
      <ReadOnlyKycField label={idLabel} value={doc?.identificationNo} />
      {startLabel && (
        <div className="flex gap-3 w-full">
          <ReadOnlyKycField label={startLabel} value={fmtDate(doc?.startDate)} />
          {endLabel && <ReadOnlyKycField label={endLabel} value={fmtDate(doc?.endDate)} />}
        </div>
      )}
      <div className="flex gap-4 w-full">
        <SavedDocCard label="Front Side" attachment={doc?.attachments?.[0]} onView={onView} />
        {doc?.attachments?.[1] ? <SavedDocCard label="Back Side" attachment={doc.attachments[1]} onView={onView} /> : <div className="flex flex-1 items-center justify-center p-4 rounded-[14px] border border-dashed border-[#d1d5db] text-[#9ca3af] font-['Outfit'] text-[12px]">Back Side not uploaded</div>}
      </div>
      <div className={`flex items-center justify-center gap-2 rounded-[12px] py-2 border ${status.style}`}>
        <span className="text-[13px]">{status.icon}</span>
        <span className="font-['Outfit'] font-bold text-[13px]">{status.label}</span>
      </div>
    </div>
  </div>
  );
};

/* ─── upload box ─── */
export const UploadBox = ({ label, file, preview, onChange }) => {
  const ref = useRef(null);
  return (
    <div
      className="bg-white border-[0.4px] border-dashed border-[#0d9488] flex flex-col gap-2 h-[178px] items-center justify-center flex-1 rounded-[14px] min-w-0 p-[12px] cursor-pointer"
      onClick={() => ref.current?.click()}
    >
      <input type="file" accept="image/*,application/pdf" className="hidden" ref={ref} onChange={onChange} />
      {preview ? (
        <img src={preview} alt={label} className="h-[90px] w-full object-contain rounded-[8px]" />
      ) : (
        <div className="bg-[#ccfbf1] flex items-center justify-center rounded-full size-[52px] shrink-0">
          <Camera size={24} className="text-[#0d9488]" />
        </div>
      )}
      <div className="flex flex-col gap-1 items-center">
        <p className="font-['Outfit'] font-bold text-[#111827] text-[14px]">{label}</p>
        <p className="font-['Outfit'] font-normal text-[#9ca3af] text-[12px] truncate max-w-full">{file ? file.name : "Tap to upload"}</p>
      </div>
      {!preview && (
        <button type="button" className="border border-[#0d9488] border-solid px-5 py-2 rounded-[8px] font-['Outfit'] font-semibold text-[#6b7280] text-[12px] bg-transparent">
          Choose File
        </button>
      )}
    </div>
  );
};

/* ─── scrollbar beside a section: the page itself scrolls, so this tracks window scroll across the section ─── */
const SCROLLBAR_THUMB_H = 36;
const SCROLLBAR_TOP = 96; // matches the page's pt-24

const clamp01 = (n) => Math.min(1, Math.max(0, n));

const getScrollRange = (el) => {
  const rect = el.getBoundingClientRect();
  const start = rect.top + window.scrollY - SCROLLBAR_TOP;
  return { start, span: rect.bottom + window.scrollY - window.innerHeight - start };
};

export const SectionScrollbar = ({ targetRef }) => {
  const [ratio, setRatio] = useState(null); // null: the section already fits, nothing to scroll

  useEffect(() => {
    const target = targetRef.current;
    const update = () => {
      const { start, span } = getScrollRange(target);
      setRatio(span > 0 ? clamp01((window.scrollY - start) / span) : null);
    };
    update();
    const observer = new ResizeObserver(update); // forms and panels change the section height
    observer.observe(target);
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [targetRef]);

  const scrollToPointer = (e) => {
    // The track moves with the page, so map from the viewport to keep the thumb under the pointer while dragging
    const next = clamp01((e.clientY - SCROLLBAR_TOP - SCROLLBAR_THUMB_H / 2) / (window.innerHeight - SCROLLBAR_TOP - SCROLLBAR_THUMB_H));
    const { start, span } = getScrollRange(targetRef.current);
    window.scrollTo({ top: start + next * span, behavior: "instant" });
  };

  if (ratio === null) return null;

  return (
    /* Sits in the 24px gap left of the section; the parent must be position: relative */
    <div
      aria-hidden="true"
      onPointerDown={(e) => { e.preventDefault(); e.currentTarget.setPointerCapture(e.pointerId); scrollToPointer(e); }}
      onPointerMove={(e) => { if (e.currentTarget.hasPointerCapture(e.pointerId)) scrollToPointer(e); }}
      className="absolute bottom-0 cursor-pointer flex justify-center left-[-20px] top-0 touch-none w-4"
    >
      <div className="bg-[#e5e7eb] h-full relative rounded-full w-1">
        <div
          className="absolute bg-[#7C3AED] left-0 rounded-full w-full"
          style={{ height: SCROLLBAR_THUMB_H, top: `calc(${ratio} * (100% - ${SCROLLBAR_THUMB_H}px))` }}
        />
      </div>
    </div>
  );
};

/* ─── booking type flags, resolved from catalogValues ─── */
export const getBookingTypeInfo = (data) => {
  const cv = (() => { try { return JSON.parse(localStorage.getItem("catalogValues") || "{}"); } catch { return {}; } })();
  const bookingTypeIds = cv?.bookingTypes?.inverted || {};
  const isElectricCar = Number(data?.bookingType) === Number(bookingTypeIds.electricCars);
  /* fuelType may be a catalog id or missing on the listing payload; without a fuel name, go by the booking type */
  const fuelId = data?.vehicleInfo?.fuelType;
  const fuel = data?.vehicleInfo?.fuelTypeText || cv?.fuelType?.[fuelId] || (Number.isNaN(Number(fuelId)) ? fuelId : "");
  return {
    bookingTypeLabel: cv?.bookingTypes?.[data?.bookingType] || null,
    isDailyRental: Number(data?.bookingType) === Number(bookingTypeIds.dailyRental ?? 714),
    isShortSub: Number(data?.bookingType) === Number(bookingTypeIds.shortSubscription ?? 715),
    isSubscription: Number(data?.bookingType) === Number(bookingTypeIds.subscription ?? 716),
    isElectricCar,
    isEv: fuel ? /electric|^ev$/i.test(fuel) : isElectricCar,
  };
};

export const isEvVehicle = (vehicle) => /electric|^ev$/i.test(vehicle?.fuelType || "");

/* ─── driving range chip text; only for EVs with a known range ─── */
export const getEvRangeLabel = (vehicle) =>
  isEvVehicle(vehicle) && vehicle?.drivingRange
    ? `Driving Range : ${vehicle.drivingRange} Km`
    : null;

/* ─── booking's vehicle with body type / driving range from bookingvalues; that holds the last car picked, so skip it for a different vehicle ─── */
export const getBookingVehicle = (booking) => {
  const vehicle = booking?.vehicleDetails;
  const saved = (() => { try { return JSON.parse(localStorage.getItem("bookingvalues") || "{}"); } catch { return {}; } })();
  const vehicleId = booking?.vehicleId ?? vehicle?.id;
  if (vehicleId != null && Number(vehicleId) !== Number(saved?.vehicleId)) return vehicle;
  return {
    ...vehicle,
    fuelType: vehicle?.fuelType || saved?.fuelType,
    bodyTypeText: saved?.bodyTypeText,
    drivingRange: saved?.drivingRange,
  };
};

/* ─── package label; subscriptions have no KM package ─── */
export const getPackageLabel = (booking) => {
  const { isShortSub, isSubscription } = getBookingTypeInfo(booking);
  return isShortSub || isSubscription ? "Subscription Plan" : `${booking?.packageDetails?.name || "—"} KM`;
};

/* ─── booking type tag for a card's top-right corner; the parent must be relative + overflow-hidden.
   `inline` renders it in normal flow instead, with a flat tag in place of the corner ribbon ─── */
export const BookingTypeTag = ({ booking, inline = false }) => {
  const { bookingTypeLabel, isDailyRental, isShortSub, isSubscription, isElectricCar } = getBookingTypeInfo(booking);

  if (isShortSub) {
    if (inline) {
      return (
        <div className="bg-[#e91d21] px-3 py-1 rounded-lg shrink-0 pointer-events-none">
          <span className="block font-['Outfit'] font-semibold text-white text-[12px] whitespace-nowrap">{bookingTypeLabel || "Short Subscription"}</span>
        </div>
      );
    }
    return (
      <div className="absolute top-0 right-0 w-[140px] h-[140px] overflow-hidden pointer-events-none" style={{ zIndex: 1 }}>
        <div
          className="absolute bg-[#e91d21] text-white font-['Outfit'] font-semibold text-[10px] text-center shadow-md py-1.5 whitespace-pre-line leading-tight"
          style={{ width: "180px", right: "-48px", top: "-2px", transform: "rotate(26.34deg)", letterSpacing: "-0.1px" }}
        >
          {bookingTypeLabel ? bookingTypeLabel.replace(" ", "\n") : "Short\nSubscription"}
        </div>
      </div>
    );
  }

  if (isSubscription) {
    return (
      <div className={`${inline ? "shrink-0" : "absolute top-0 right-2"} pointer-events-none`} style={{ zIndex: 1 }}>
        <div
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-bl-lg rounded-br-lg rounded-tr-lg font-['Outfit'] font-semibold text-[#46390c] text-[10px] whitespace-nowrap shadow-sm"
          style={{ background: "linear-gradient(93.78deg, #FFFE89 1.06%, #E9BF37 65.49%)", border: "1px solid #836b1e", letterSpacing: "-0.1px" }}
        >
          {bookingTypeLabel || "Subscription"}
          <img src="/assets/profile/mob/crown.svg" alt="" width={14} height={14} className="block max-w-none shrink-0" />
        </div>
      </div>
    );
  }

  if (isDailyRental) {
    return (
      <div className={`${inline ? "shrink-0" : "absolute top-3 right-3"} bg-[#70c416] px-3 py-1 rounded-lg pointer-events-none`} style={{ zIndex: 1 }}>
        <span className="block font-['Outfit'] font-medium text-white text-[12px] whitespace-nowrap">{bookingTypeLabel || "Daily Rental"}</span>
      </div>
    );
  }

  if (isElectricCar) {
    return (
      <div className={`${inline ? "shrink-0" : "absolute top-3 right-3"} bg-white border border-[#70c416] px-[10px] py-1.5 rounded-[18px] pointer-events-none`} style={{ zIndex: 1 }}>
        <span className="block font-['Outfit'] font-semibold text-[#70c416] text-[12px] whitespace-nowrap">EV Car</span>
      </div>
    );
  }

  return null;
};

/* ─── which status screen a booking opens: refund / extend / booking status ─── */
export const getStatusView = (data, filter) => {
  if (filter?.inverted?.cancelled?.includes(data?.bookingStatus) ||
      filter?.inverted?.refundPending?.includes(data?.bookingStatus) ||
      filter?.inverted?.refundInitiated?.includes(data?.bookingStatus) ||
      filter?.inverted?.refundCompleted?.includes(data?.bookingStatus) ||
      filter?.inverted?.bookingClosed?.includes(data?.bookingStatus)) return "refundStatus";
  if (filter?.inverted?.bookingExtented?.includes(data?.bookingStatus)) return "extendStatus";
  return "viewStatus";
};

/* ─── status label from the catalog; these statuses get a neutral badge instead of green ─── */
const NEUTRAL_STATUS_KEYS = ["inquiry", "paymentPending", "inquiryClosed", "cancelled", "refundPending", "refundInitiated"];

export const getBookingStatusInfo = (booking) => {
  if (!booking) return { label: "Pending", isPositive: false };
  const cv = (() => { try { return JSON.parse(localStorage.getItem("catalogValues") || "{}"); } catch { return {}; } })();
  const statuses = cv?.fleetBookingStatus;
  const isNeutral = NEUTRAL_STATUS_KEYS.some((key) => [].concat(statuses?.inverted?.[key]).map(Number).includes(Number(booking.bookingStatus)));
  return { label: statuses?.[booking.bookingStatus] || "Upcoming", isPositive: !isNeutral };
};

export const StatusBadge = ({ booking }) => {
  const { label, isPositive } = getBookingStatusInfo(booking);
  return (
    <div className={`border px-3 py-1.5 rounded-lg shrink-0 ${isPositive ? "bg-[rgba(16,185,129,0.08)] border-[#a3ecd8]" : "bg-[#f3f4f6] border-[#e5e7eb]"}`}>
      <span className={`block font-semibold text-[12px] whitespace-nowrap ${isPositive ? "text-[#10b981]" : "text-[#6b7280]"}`}>{label}</span>
    </div>
  );
};
